import { useState, useEffect, useRef } from "react";
import "./App.css";

const NAV_LINKS = ["Home", "About", "Games", "Services", "Contact"];

const GAMES = [
  {
    id: 1,
    title: "Action & Adventure",
    desc: "Heart-pounding games with fluid mechanics, immersive worlds, and player-first design.",
    icon: "⚔️",
    tag: "Genre",
  },
  {
    id: 2,
    title: "Puzzle & Strategy",
    desc: "Mind-bending challenges that reward smart thinking and keep players coming back.",
    icon: "🧩",
    tag: "Genre",
  },
  {
    id: 3,
    title: "Casual & Hyper-Casual",
    desc: "Easy to pick up, impossible to put down — built for mass-market engagement.",
    icon: "🎮",
    tag: "Genre",
  },
  {
    id: 4,
    title: "RPG & Simulation",
    desc: "Deep progression systems, world-building, and stories that players live inside.",
    icon: "🌍",
    tag: "Genre",
  },
];

const SERVICES = [
  { icon: "🎯", title: "Game Design", desc: "Complete game design documentation, mechanics, and player journey mapping." },
  { icon: "🛠️", title: "Development", desc: "Full-cycle Android game development published on Google Play Store." },
  { icon: "📊", title: "Analytics & Growth", desc: "Data-driven UA strategies, retention analysis, and monetization optimization." },
  { icon: "🤝", title: "Publishing Support", desc: "End-to-end Play Store publishing — from listing to launch day." },
];

const BADGES = [
  { label: "Google Play Developer", color: "#34A853" },
  { label: "Udhyam Registered", color: "#F97316" },
  { label: "DUNS Registered", color: "#1e6fbf" },
];

// ─── Carousel ─────────────────────────────────────────────────────────────────
function Carousel() {
  const [active, setActive] = useState(0);
  const timer = useRef(null);

  const next = () => setActive((p) => (p + 1) % GAMES.length);
  const prev = () => setActive((p) => (p - 1 + GAMES.length) % GAMES.length);

  useEffect(() => {
    timer.current = setInterval(next, 3500);
    return () => clearInterval(timer.current);
  }, []);

  const resetTimer = (fn) => {
    clearInterval(timer.current);
    fn();
    timer.current = setInterval(next, 3500);
  };

  return (
    <div className="carousel">
      <div className="carousel-track">
        {GAMES.map((g, i) => (
          <div
            key={g.id}
            className={`carousel-card ${
              i === active
                ? "active"
                : i === (active + 1) % GAMES.length
                ? "next"
                : i === (active - 1 + GAMES.length) % GAMES.length
                ? "prev"
                : "hidden"
            }`}
          >
            <span className="carousel-icon">{g.icon}</span>
            <span className="carousel-tag">{g.tag}</span>
            <h3>{g.title}</h3>
            <p>{g.desc}</p>
          </div>
        ))}
      </div>
      <div className="carousel-controls">
        <button className="carousel-btn" onClick={() => resetTimer(prev)}>←</button>
        <div className="carousel-dots">
          {GAMES.map((_, i) => (
            <button
              key={i}
              className={`dot ${i === active ? "active" : ""}`}
              onClick={() => resetTimer(() => setActive(i))}
            />
          ))}
        </div>
        <button className="carousel-btn" onClick={() => resetTimer(next)}>→</button>
      </div>
    </div>
  );
}

// ─── Logo Component ───────────────────────────────────────────────────────────
function Logo({ size = "md", onClick }) {
  const sizes = { sm: 36, md: 44, lg: 56 };
  const px = sizes[size] || 44;
  return (
    <div className="logo" onClick={onClick} style={{ cursor: onClick ? "pointer" : "default" }}>
      <img
        src="/my main logo.png"
        alt="Ayush Game Labs Logo"
        style={{ height: px, width: "auto", display: "block", objectFit: "contain" }}
      />
    </div>
  );
}

// ─── App ──────────────────────────────────────────────────────────────────────
export default function App() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [visibleSections, setVisibleSections] = useState(new Set());

  const EMAIL = "ayush@ayushgamelabs.site";
  const WHATSAPP_DISPLAY = "+91 90546 37382";
  const WA_NUMBER = "919054637382";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            setVisibleSections((prev) => new Set([...prev, e.target.dataset.section]));
          }
        });
      },
      { threshold: 0.12 }
    );
    document.querySelectorAll("[data-section]").forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, []);

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    setMenuOpen(false);
  };

  const isVisible = (name) => visibleSections.has(name);

  return (
    <div className="app">
      {/* ── NAV ── */}
      <nav className={`nav ${scrolled ? "nav-scrolled" : ""}`}>
        <div className="nav-inner">
          <Logo size="md" onClick={() => scrollTo("home")} />
          <ul className={`nav-links ${menuOpen ? "open" : ""}`}>
            {NAV_LINKS.map((l) => (
              <li key={l}>
                <button onClick={() => scrollTo(l.toLowerCase())}>{l}</button>
              </li>
            ))}
          </ul>
          <button className="hamburger" onClick={() => setMenuOpen((o) => !o)}>
            <span /><span /><span />
          </button>
        </div>
      </nav>

      {/* ── HERO ── */}
      <section id="home" className="hero" data-section="hero">
        <div className={`hero-content fade-up ${isVisible("hero") ? "visible" : ""}`}>
          <div className="hero-badge">Independent Game Studio · Google Play</div>
          <h1 className="hero-title">
            We Build Games<br />
            <span className="gradient-text">People Play Every Day</span>
          </h1>
          <p className="hero-sub">
            Ayush Game Labs is a certified, registered indie studio crafting engaging Android games — from concept to Play Store.
          </p>
          <div className="hero-cta">
            <a href={`mailto:${EMAIL}`} className="btn btn-primary">Get In Touch</a>
            <button className="btn btn-outline" onClick={() => scrollTo("games")}>Explore Games</button>
          </div>
          <div className="hero-badges">
            {BADGES.map((b) => (
              <span key={b.label} className="trust-badge" style={{ "--badge-color": b.color }}>
                <span className="badge-dot" /> {b.label}
              </span>
            ))}
          </div>
        </div>

        <div className={`hero-visual fade-up ${isVisible("hero") ? "visible" : ""}`} style={{ animationDelay: "0.2s" }}>
          <div className="visual-card">
            <div className="vc-screen">
              <img
                src="/my main logo.png"
                alt="Ayush Game Labs"
                style={{ width: "72%", height: "auto", objectFit: "contain", filter: "drop-shadow(0 8px 24px rgba(30,111,191,0.18))" }}
              />
              <div className="vc-label">Ayush Game Labs</div>
            </div>
          </div>
          <div className="floating-stat s1">
            <span className="stat-num">100%</span>
            <span className="stat-lbl">Play Store Ready</span>
          </div>
          <div className="floating-stat s2">
            <span className="stat-num">DUNS</span>
            <span className="stat-lbl">Verified</span>
          </div>
        </div>
      </section>

      {/* ── ABOUT ── */}
      <section id="about" className="section about" data-section="about">
        <div className={`section-inner fade-up ${isVisible("about") ? "visible" : ""}`}>
          <div className="section-label">Who We Are</div>
          <div className="about-grid">
            <div className="about-text">
              <h2>A Registered Studio Built for the Mobile Era</h2>
              <p>
                Ayush Game Labs is an Udhyam-registered and DUNS-certified independent game development studio. We specialize in Android gaming — designing, building, and publishing titles that combine great mechanics with player-first thinking.
              </p>
              <p>
                Every game we ship goes through rigorous testing, compliance checks, and Play Store optimization so it's discoverable, stable, and loved.
              </p>
            </div>
            <div className="about-stats">
              {[
                { num: "Android", lbl: "Platform Focus" },
                { num: "Play Store", lbl: "Publishing" },
                { num: "MSME", lbl: "Udhyam Certified" },
                { num: "D‑U‑N‑S", lbl: "Business Verified" },
              ].map((s) => (
                <div className="stat-card" key={s.num}>
                  <div className="stat-card-num">{s.num}</div>
                  <div className="stat-card-lbl">{s.lbl}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── GAMES / CAROUSEL ── */}
      <section id="games" className="section games" data-section="games">
        <div className={`section-inner fade-up ${isVisible("games") ? "visible" : ""}`}>
          <div className="section-label">What We Make</div>
          <h2 className="section-title">Game Categories We Excel In</h2>
          <p className="section-sub">From casual taps to deep RPG worlds — we design for every kind of player.</p>
          <Carousel />
        </div>
      </section>

      {/* ── SERVICES ── */}
      <section id="services" className="section services" data-section="services">
        <div className={`section-inner fade-up ${isVisible("services") ? "visible" : ""}`}>
          <div className="section-label">What We Offer</div>
          <h2 className="section-title">End-to-End Game Services</h2>
          <div className="services-grid">
            {SERVICES.map((s, i) => (
              <div className="service-card" key={s.title} style={{ animationDelay: `${i * 0.1}s` }}>
                <span className="service-icon">{s.icon}</span>
                <h3>{s.title}</h3>
                <p>{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CONTACT ── */}
      <section id="contact" className="section contact" data-section="contact">
        <div className={`section-inner fade-up ${isVisible("contact") ? "visible" : ""}`}>
          <div className="section-label">Reach Us</div>
          <h2 className="section-title">Let's Build Something Great</h2>
          <p className="section-sub">Have an idea? Want to collaborate? We're one message away.</p>
          <div className="contact-cards">
            <a href={`mailto:${EMAIL}`} className="contact-card email-card">
              <span className="contact-card-icon">✉️</span>
              <div className="contact-card-info">
                <span className="contact-card-label">Email Us</span>
                <span className="contact-card-value">{EMAIL}</span>
              </div>
              <span className="contact-card-arrow">→</span>
            </a>
            <a
              href={`https://wa.me/${WA_NUMBER}?text=Hi%20Ayush%20Game%20Labs%2C%20I%27d%20like%20to%20connect!`}
              target="_blank"
              rel="noreferrer"
              className="contact-card wa-card"
            >
              <span className="contact-card-icon">💬</span>
              <div className="contact-card-info">
                <span className="contact-card-label">WhatsApp</span>
                <span className="contact-card-value">{WHATSAPP_DISPLAY}</span>
              </div>
              <span className="contact-card-arrow">→</span>
            </a>
          </div>
          <div className="contact-cta-row">
            <a href={`mailto:${EMAIL}?subject=Collaboration%20Inquiry`} className="btn btn-primary btn-lg">
              ✉️ &nbsp;Contact via Email
            </a>
            <a
              href={`https://wa.me/${WA_NUMBER}?text=Hi%20Ayush%20Game%20Labs%2C%20I%27d%20like%20to%20connect!`}
              target="_blank"
              rel="noreferrer"
              className="btn btn-wa btn-lg"
            >
              💬 &nbsp;Contact via WhatsApp
            </a>
          </div>
        </div>
      </section>

      {/* ── FOOTER ── */}
      <footer className="footer">
        <div className="footer-inner">
          <Logo size="sm" />
          <p className="footer-tagline">Crafting Android Games That Players Love</p>
          <p className="footer-copy">
            © {new Date().getFullYear()} Ayush Game Labs. Udhyam Registered · DUNS Certified · All rights reserved.
          </p>
          <div className="footer-links">
            {["home", "about", "games", "services", "contact"].map((l) => (
              <button key={l} onClick={() => scrollTo(l)}>
                {l.charAt(0).toUpperCase() + l.slice(1)}
              </button>
            ))}
          </div>
          <div className="footer-contact-row">
            <a href={`mailto:${EMAIL}`} className="footer-contact-link">✉️ {EMAIL}</a>
            <a
              href={`https://wa.me/${WA_NUMBER}`}
              target="_blank"
              rel="noreferrer"
              className="footer-contact-link"
            >
              💬 {WHATSAPP_DISPLAY}
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}